import { useState } from "react";
import {
  Building2,
  Target,
  Users,
  Clock,
  ArrowLeft,
  Briefcase,
  FileText,
} from "lucide-react";
import { AdvaLogo } from "@adva/brand-assets/components";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-20">
      <h2 className="font-display text-title font-light text-text-hi mb-4">
        {title}
      </h2>
      {description && (
        <p className="typo-body text-text-md mb-8 max-w-3xl">{description}</p>
      )}
      <div className="rounded-lg border border-border bg-surface p-6 shadow-sm">
        {children}
      </div>
    </section>
  );
}

function AnnotatedToken({
  label,
  typoClass,
  token,
}: {
  label: string;
  typoClass: string;
  token: string;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-border pb-2">
      <span className="typo-body text-text-md">{label}</span>
      <div className="flex items-baseline gap-3">
        <code className="typo-mono text-text-lo">.{typoClass}</code>
        <code className="typo-mono text-accent">{token}</code>
      </div>
    </div>
  );
}

const TYPE_SCALE = [
  {
    token: "display",
    typoClass: "typo-display",
    sample: "Adva Platform",
    use: "Marketing hero",
    family: "Space Grotesk 300",
    size: "72/64",
  },
  {
    token: "title",
    typoClass: "typo-title",
    sample: "Cherrywood Estates HOA",
    use: "Page title (detail pages), KPI numbers",
    family: "Space Grotesk 300",
    size: "40/44",
  },
  {
    token: "heading",
    typoClass: "typo-heading",
    sample: "Business Details",
    use: "Section headers, card titles, settings page titles",
    family: "Inter 600",
    size: "24/30",
  },
  {
    token: "body",
    typoClass: "typo-body",
    sample:
      "Core settings for your business. Manage timezone, currency, and name.",
    use: "Default body, descriptions, nav items, subtitles",
    family: "Inter 400",
    size: "14/22",
  },
  {
    token: "label",
    typoClass: "typo-label",
    sample: "BUSINESS NAME",
    use: "Form labels, table headers, group labels, badges",
    family: "Inter 500, uppercase, +0.08em",
    size: "11/14",
  },
  {
    token: "data",
    typoClass: "typo-mono",
    sample: "$184,500.00 · OPP-1172 · 2026-04-28",
    use: "Table cells (numbers), IDs, timestamps, currency",
    family: "Geist Mono 400, tabular-nums",
    size: "14/20",
  },
];

function TypeScaleSection() {
  return (
    <div className="space-y-6">
      {TYPE_SCALE.map((row) => (
        <div
          key={row.token}
          className="border-b border-border pb-6 last:border-b-0"
        >
          <div className="mb-2 flex items-baseline justify-between gap-4">
            <code className="typo-mono text-accent">{row.token}</code>
            <div className="flex items-baseline gap-3">
              <code className="typo-mono text-text-lo">.{row.typoClass}</code>
              <span className="typo-mono text-text-lo">{row.size}</span>
            </div>
          </div>
          <div className={row.typoClass}>{row.sample}</div>
          <div className="mt-2 flex items-baseline justify-between">
            <span className="typo-body text-text-md">{row.use}</span>
            <span className="typo-mono text-text-lo">{row.family}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function PageHeaderExample({ variant }: { variant: "settings" | "detail" }) {
  if (variant === "settings") {
    return (
      <div className="space-y-1">
        <Breadcrumb className="mb-2 typo-body">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink className="text-text-lo">Settings</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="text-text-lo">
              /
            </BreadcrumbSeparator>
            <BreadcrumbItem>
              <BreadcrumbPage>General</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <h1 className="typo-heading text-text-hi">General</h1>
        <p className="typo-body text-text-md">
          Business name, timezone, and currency.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Breadcrumb className="typo-body">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink className="text-text-lo">
              Canopy Home Services
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator className="text-text-lo">/</BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbLink className="text-text-lo">
              Opportunities
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator className="text-text-lo">/</BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage>OPP-1172</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <h1 className="typo-title text-text-hi">
        Cherrywood Estates HOA — Full-property renovation
      </h1>
      <div className="flex items-center gap-3 typo-body text-text-md">
        <span className="typo-mono text-text-hi">OPP-1172</span>
        <Badge
          variant="outline"
          className="border-warning text-warning typo-label"
        >
          IN PROGRESS
        </Badge>
        <span>·</span>
        <span>Project</span>
        <span>·</span>
        <span>Cherrywood Estates HOA</span>
      </div>
      <div className="typo-body text-text-lo">
        Rep Maria Alvarez · Opened 12 weeks ago
      </div>
    </div>
  );
}

function FormExample() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Business Details</CardTitle>
        <CardDescription>Core settings for your business.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <label className="typo-label text-text-hi">BUSINESS NAME</label>
          <Input placeholder="Canopy Turf" defaultValue="Canopy Turf" />
        </div>
        <div className="space-y-1.5">
          <label className="typo-label text-text-hi">BUSINESS EMAIL</label>
          <Input
            placeholder="hello@canopyturf.com"
            defaultValue="hello@canopyturf.com"
          />
        </div>
        <div className="flex justify-end pt-2">
          <Button>Save</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function KPICardExample() {
  return (
    <div className="grid grid-cols-2 gap-4">
      <Card>
        <CardContent className="p-4">
          <p className="typo-label text-text-lo">QUOTED</p>
          <p className="typo-title text-accent mt-1">$184,500</p>
          <p className="typo-body text-text-lo mt-1">
            2 proposals · latest Feb 14
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4">
          <p className="typo-label text-text-lo">EARNED</p>
          <p className="typo-title text-success mt-1">$92,250</p>
          <p className="typo-body text-text-lo mt-1">2 jobs · $46,125 avg</p>
        </CardContent>
      </Card>
    </div>
  );
}

function DataTableExample() {
  const rows = [
    {
      id: "OPP-1172",
      name: "Cherrywood Estates HOA",
      status: "In Progress",
      value: "$184,500",
      date: "2026-02-14",
    },
    {
      id: "OPP-1098",
      name: "Riverside Corporate Park",
      status: "Won",
      value: "$92,750",
      date: "2026-01-28",
    },
    {
      id: "OPP-1045",
      name: "Oakmont Residential",
      status: "Lost",
      value: "$67,200",
      date: "2025-12-15",
    },
  ];

  const statusColor: Record<string, string> = {
    "In Progress": "text-warning border-warning",
    Won: "text-success border-success",
    Lost: "text-danger border-danger",
  };

  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="px-4 py-2 text-left typo-label text-text-lo">
                ID
              </th>
              <th className="px-4 py-2 text-left typo-label text-text-lo">
                NAME
              </th>
              <th className="px-4 py-2 text-left typo-label text-text-lo">
                STATUS
              </th>
              <th className="px-4 py-2 text-right typo-label text-text-lo">
                VALUE
              </th>
              <th className="px-4 py-2 text-right typo-label text-text-lo">
                DATE
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className="border-b border-border last:border-b-0"
              >
                <td className="px-4 py-2.5 typo-mono text-text-md">{row.id}</td>
                <td className="px-4 py-2.5 typo-body text-text-hi">
                  {row.name}
                </td>
                <td className="px-4 py-2.5">
                  <Badge
                    variant="outline"
                    className={`typo-label ${statusColor[row.status]}`}
                  >
                    {row.status.toUpperCase()}
                  </Badge>
                </td>
                <td className="px-4 py-2.5 text-right typo-mono text-text-hi">
                  {row.value}
                </td>
                <td className="px-4 py-2.5 text-right typo-mono text-text-lo">
                  {row.date}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function SidebarNavExample() {
  const personalItems = [
    { icon: Users, label: "Profile", active: false },
    { icon: Target, label: "Preferences", active: false },
  ];
  const businessItems = [
    { icon: Building2, label: "General", active: true },
    { icon: Building2, label: "Business Profile", active: false },
    { icon: Clock, label: "Hours", active: false },
  ];

  return (
    <div className="w-56 rounded-lg bg-surface border border-border p-3 space-y-4">
      <a className="flex items-center gap-2 typo-body text-text-lo">
        <ArrowLeft className="h-4 w-4" />
        Back to app
      </a>
      <div className="space-y-1">
        {personalItems.map((item) => (
          <div
            key={item.label}
            className={`flex items-center gap-2 rounded-md px-2 py-1.5 typo-body ${
              item.active ? "bg-elevated text-text-hi" : "text-text-lo"
            }`}
          >
            <item.icon className="h-4 w-4" />
            <span>{item.label}</span>
          </div>
        ))}
      </div>
      <div className="space-y-1">
        <p className="typo-label text-text-lo px-2 mb-1">CANOPY TURF</p>
        {businessItems.map((item) => (
          <div
            key={item.label}
            className={`flex items-center gap-2 rounded-md px-2 py-1.5 typo-body ${
              item.active ? "bg-elevated text-text-hi" : "text-text-lo"
            }`}
          >
            <item.icon className="h-4 w-4" />
            <span>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MainNavExample() {
  const items = [
    { icon: Target, label: "Opportunities", count: 38, active: true },
    { icon: Briefcase, label: "Jobs", count: 47, active: false },
    { icon: FileText, label: "Invoices", count: 14, active: false },
    { icon: Users, label: "Customers", count: 89, active: false },
  ];

  return (
    <div className="w-56 rounded-lg bg-surface border border-border p-2 space-y-0.5">
      {items.map((item) => (
        <div
          key={item.label}
          className={`flex items-center gap-2 rounded-md px-3 py-1.5 typo-body ${
            item.active
              ? "border-l-2 border-l-accent bg-elevated text-text-hi"
              : "border-l-2 border-l-transparent text-text-lo"
          }`}
        >
          <item.icon className="h-4 w-4 shrink-0" strokeWidth={1.5} />
          <span className="flex-1 truncate">{item.label}</span>
          {item.count != null && (
            <span className="typo-mono text-text-lo">{item.count}</span>
          )}
        </div>
      ))}
    </div>
  );
}

function TokenAnnotation() {
  const tokens = [
    {
      label: "Page title (detail)",
      typoClass: "typo-title",
      vars: "--font-family-display · --font-size-title · 300",
    },
    {
      label: "Page title (settings)",
      typoClass: "typo-heading",
      vars: "--font-family-body · --font-size-heading · 600",
    },
    {
      label: "Card / section title",
      typoClass: "typo-heading",
      vars: "--font-family-body · --font-size-heading · 600",
    },
    {
      label: "Body text, descriptions",
      typoClass: "typo-body",
      vars: "--font-family-body · --font-size-body",
    },
    {
      label: "Form labels, table headers",
      typoClass: "typo-label",
      vars: "--font-family-body · --font-size-label · 500 · uppercase",
    },
    {
      label: "Numbers, IDs, timestamps",
      typoClass: "typo-mono",
      vars: "--font-family-mono · --font-size-data · tabular-nums",
    },
  ];

  return (
    <div className="space-y-2">
      {tokens.map((t) => (
        <AnnotatedToken
          key={t.label}
          label={t.label}
          typoClass={t.typoClass}
          token={t.vars}
        />
      ))}
    </div>
  );
}

export default function V2DevTypographyPage() {
  const [theme, setTheme] = useState<"dark" | "light">("dark");

  return (
    <div
      className="min-h-screen bg-bg text-text-hi font-body"
      data-theme={theme}
    >
      <div className="mx-auto max-w-6xl px-6 py-16 md:px-10 md:py-24">
        <header className="mb-24 flex flex-col items-start gap-8">
          <div className="flex w-full items-start justify-between">
            <AdvaLogo
              layout="horizontal"
              size={128}
              color={theme === "dark" ? "dark" : "light"}
            />
            <div className="flex items-center gap-1 rounded-lg border border-border bg-surface p-1">
              <button
                onClick={() => setTheme("dark")}
                className={`rounded-md px-3 py-1.5 typo-label transition-colors ${
                  theme === "dark"
                    ? "bg-elevated text-text-hi"
                    : "text-text-lo hover:text-text-md"
                }`}
              >
                Dark
              </button>
              <button
                onClick={() => setTheme("light")}
                className={`rounded-md px-3 py-1.5 typo-label transition-colors ${
                  theme === "light"
                    ? "bg-elevated text-text-hi"
                    : "text-text-lo hover:text-text-md"
                }`}
              >
                Light
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <p className="font-mono text-label uppercase tracking-label text-accent">
              Design system · Component typography
            </p>
            <h1 className="font-display text-display font-light text-text-hi tracking-display">
              Typography &amp; Components
            </h1>
            <p className="font-body text-heading text-text-md max-w-2xl">
              Living reference for how design tokens map to product UI elements.
              Complements{" "}
              <a href="/brand" className="text-accent hover:underline">
                /brand
              </a>{" "}
              (token swatches) with in-context component examples. See ADR-0018
              Amendment B for the full mapping table.
            </p>
          </div>
        </header>

        <Section title="Type scale">
          <TypeScaleSection />
        </Section>

        <Section title="Component mapping">
          <TokenAnnotation />
        </Section>

        <Section
          title="Page headers"
          description={
            <>
              Settings pages use{" "}
              <code className="typo-mono text-accent">typo-heading</code> (Inter
              600, 24px). Entity detail pages use{" "}
              <code className="typo-mono text-accent">typo-title</code> (Space
              Grotesk 300, 40px) for greater visual weight.
            </>
          }
        >
          <div className="space-y-8">
            <div>
              <p className="typo-label text-text-lo mb-3">
                Settings page header
              </p>
              <PageHeaderExample variant="settings" />
            </div>
            <div className="border-t border-border pt-8">
              <p className="typo-label text-text-lo mb-3">Detail page header</p>
              <PageHeaderExample variant="detail" />
            </div>
          </div>
        </Section>

        <Section
          title="Cards & forms"
          description={
            <>
              Card titles use{" "}
              <code className="typo-mono text-accent">typo-heading</code>. Form
              labels use{" "}
              <code className="typo-mono text-accent">typo-label</code>{" "}
              (uppercase, 11px). Input text inherits{" "}
              <code className="typo-mono text-accent">typo-body</code>.
            </>
          }
        >
          <div className="max-w-lg">
            <FormExample />
          </div>
        </Section>

        <Section
          title="KPI cards"
          description={
            <>
              Large numbers use{" "}
              <code className="typo-mono text-accent">typo-title</code> (Space
              Grotesk 300, 40px) for the hero metric. Labels use{" "}
              <code className="typo-mono text-accent">typo-label</code>.
              Supporting text uses{" "}
              <code className="typo-mono text-accent">typo-body</code>.
            </>
          }
        >
          <KPICardExample />
        </Section>

        <Section
          title="Data tables"
          description={
            <>
              Table headers use{" "}
              <code className="typo-mono text-accent">typo-label</code>. Text
              cells use <code className="typo-mono text-accent">typo-body</code>
              . Numeric cells, IDs, and dates use{" "}
              <code className="typo-mono text-accent">typo-mono</code> (Geist
              Mono, tabular-nums). Status badges use{" "}
              <code className="typo-mono text-accent">typo-label</code>.
            </>
          }
        >
          <DataTableExample />
        </Section>

        <Section
          title="Navigation"
          description={
            <>
              Nav items use{" "}
              <code className="typo-mono text-accent">typo-body</code>. Group
              labels use{" "}
              <code className="typo-mono text-accent">typo-label</code>. Badge
              counts use{" "}
              <code className="typo-mono text-accent">typo-mono</code>. Active
              state:{" "}
              <code className="typo-mono text-accent">
                text-text-hi + bg-elevated
              </code>
              . Inactive:{" "}
              <code className="typo-mono text-accent">text-text-lo</code>.
            </>
          }
        >
          <div className="flex gap-6">
            <SidebarNavExample />
            <MainNavExample />
          </div>
        </Section>

        <Section
          title="Status indicators"
          description={
            <>
              Badges and pills use{" "}
              <code className="typo-mono text-accent">typo-label</code>{" "}
              (uppercase, 11px) with semantic color tokens for border and text.
            </>
          }
        >
          <div className="flex flex-wrap gap-3">
            <Badge
              variant="outline"
              className="typo-label border-success text-success"
            >
              WON
            </Badge>
            <Badge
              variant="outline"
              className="typo-label border-warning text-warning"
            >
              IN PROGRESS
            </Badge>
            <Badge
              variant="outline"
              className="typo-label border-danger text-danger"
            >
              LOST
            </Badge>
            <Badge
              variant="outline"
              className="typo-label border-accent text-accent"
            >
              QUALIFIED
            </Badge>
            <Badge
              variant="outline"
              className="typo-label border-text-lo text-text-lo"
            >
              DRAFT
            </Badge>
            <Badge variant="secondary" className="typo-label">
              DEFAULT
            </Badge>
          </div>
        </Section>

        <Section
          title="Buttons"
          description={
            <>
              Primary button uses accent fill. Secondary uses outline. Ghost is
              text-only. All inherit{" "}
              <code className="typo-mono text-accent">typo-body</code> sizing.
            </>
          }
        >
          <div className="flex flex-wrap gap-3">
            <Button>Primary</Button>
            <Button variant="secondary">Secondary</Button>
            <Button variant="outline">Outline</Button>
            <Button variant="ghost">Ghost</Button>
            <Button variant="destructive">Destructive</Button>
          </div>
        </Section>

        <footer className="border-t border-border pt-8 mt-8">
          <p className="typo-body text-text-lo">
            ADR-0018 Amendment B · Component Typography Mapping ·{" "}
            {new Date().getFullYear()}
          </p>
        </footer>
      </div>
    </div>
  );
}
